import { useCallback, useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { Bell, Check, Monitor, Volume2, VolumeX } from '@/lib/icons';
import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import { useAuth } from '@/lib/auth';
import { subscribe } from '@/lib/dashboardEvents';
import {
  getDesktopPref, setDesktopPref, getSoundPref, setSoundPref,
  requestOsPermission, osNotify, playChime,
} from '@/lib/notificationAlerts';
import { setFaviconBadge } from '@/lib/faviconBadge';
import {
  listNotifications, unreadCount, markNotificationsRead,
  type Notification,
} from '@/lib/tasksApi';

const POLL_MS = 30_000;

const KIND_LABEL: Record<string, string> = {
  task_assigned: 'Assigned',
  task_status_changed: 'Status',
  task_commented: 'Comment',
  task_mentioned: 'Mention',
  requester_replied: 'Reply',
  task_attachment_added: 'File',
  chat_mentioned: 'Mention',
};

export function NotificationBell({ onOpenChats }: { onOpenChats?: () => void } = {}) {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [open, setOpen] = useState(false);
  const [items, setItems] = useState<Notification[]>([]);
  const [count, setCount] = useState(0);
  const [loading, setLoading] = useState(false);
  const [unreadOnly, setUnreadOnly] = useState(true);
  const [kindFilter, setKindFilter] = useState('all');
  const [desktopOn, setDesktopOn] = useState(getDesktopPref);
  const [soundOn, setSoundOn] = useState(getSoundPref);
  const popRef = useRef<HTMLDivElement | null>(null);

  // Navigate to a notification's target: a task, or — for chat notifications —
  // open the chat sheet and deep-link the thread (same `bp:open-chat-thread`
  // mechanism ChatMentionsInbox uses, consumed by ChatThreadList).
  const goTo = useCallback((n: Notification) => {
    if (n.TaskId) { navigate(`/tasks?id=${encodeURIComponent(n.TaskId)}`); return; }
    if (n.ChatThreadId) {
      onOpenChats?.();
      const threadId = n.ChatThreadId;
      window.setTimeout(() => {
        window.dispatchEvent(new CustomEvent('bp:open-chat-thread', { detail: { threadId } }));
      }, 0);
    }
  }, [navigate, onOpenChats]);

  // Lightweight count poll — reconciliation backstop behind the live WS push
  // below (covers reconnect gaps + reads marked in another tab). Heavier list
  // fetch happens only when the popover opens.
  useEffect(() => {
    let cancelled = false;
    const refreshCount = async () => {
      try {
        const r = await unreadCount();
        if (!cancelled) setCount(r.unreadCount);
      } catch { /* ignore */ }
    };
    refreshCount();
    const t = window.setInterval(refreshCount, POLL_MS);
    const onFocus = () => { void refreshCount(); };
    window.addEventListener('focus', onFocus);
    return () => {
      cancelled = true;
      window.clearInterval(t);
      window.removeEventListener('focus', onFocus);
    };
  }, []);

  // Live push: each new notification bumps the badge instantly and fires the
  // alert surfaces (in-app toast always; OS notification + chime gated by the
  // user prefs inside the helpers). The topic is self-only on the server.
  useEffect(() => {
    const uid = user?.id;
    if (!uid) return;
    return subscribe(`notifications:${uid}`, (raw) => {
      const ev = raw as { type?: string; notification?: Notification };
      if (ev.type !== 'notification' || !ev.notification) return;
      const n = ev.notification;
      setCount((c) => c + 1);
      setItems((prev) => prev.some((x) => x.Id === n.Id) ? prev : [n, ...prev]);
      const label = KIND_LABEL[n.Kind] ?? n.Kind;
      toast(n.Summary, {
        description: label,
        action: { label: 'Open', onClick: () => goTo(n) },
      });
      osNotify({ title: label, body: n.Summary, tag: n.Id, onClick: () => goTo(n) });
      playChime();
    });
  }, [user?.id, goTo]);

  // Reflect unread count in the tab title + favicon so a backgrounded tab still
  // signals new activity.
  useEffect(() => {
    document.title = count > 0 ? `(${count}) Bugpunch` : 'Bugpunch';
    setFaviconBadge(count);
  }, [count]);

  // If desktop alerts were already enabled, re-validate permission on mount so
  // a since-revoked grant doesn't silently swallow notifications.
  useEffect(() => {
    if (getDesktopPref()) void requestOsPermission();
  }, []);

  const toggleDesktop = async () => {
    const next = !desktopOn;
    if (next) {
      const perm = await requestOsPermission();
      if (perm !== 'granted') { setDesktopOn(false); setDesktopPref(false); return; }
    }
    setDesktopOn(next);
    setDesktopPref(next);
  };

  const toggleSound = () => {
    const next = !soundOn;
    setSoundOn(next);
    setSoundPref(next);
  };

  // `unread` is passed explicitly by the filter toggles (which fire before
  // their setState has settled) and defaults to current state for the open path.
  const refreshItems = useCallback(async (unread = unreadOnly) => {
    setLoading(true);
    try {
      const r = await listNotifications({ limit: 50, unread });
      setItems(r.items);
      setCount(r.unreadCount);
    } finally {
      setLoading(false);
    }
  }, [unreadOnly]);

  // Open → fetch list. Auto-mark visible items read on close, like Linear.
  const onToggle = () => {
    if (!open) void refreshItems();
    setOpen((o) => !o);
  };

  // Close on outside click.
  useEffect(() => {
    if (!open) return;
    const onDoc = (e: MouseEvent) => {
      const target = e.target as HTMLElement | null;
      if (!target) return;
      if (popRef.current && !popRef.current.contains(target)) {
        setOpen(false);
      }
    };
    window.addEventListener('mousedown', onDoc);
    return () => window.removeEventListener('mousedown', onDoc);
  }, [open]);

  const visibleItems = useMemo(
    () => kindFilter === 'all' ? items : items.filter(item => item.Kind === kindFilter),
    [items, kindFilter],
  );
  const kindOptions = useMemo(
    () => [...new Set(items.map(item => item.Kind))],
    [items],
  );
  const grouped = useMemo(() => {
    const groups: Array<{ label: string; items: Notification[] }> = [];
    for (const item of visibleItems) {
      const label = notificationDayLabel(item.CreatedAt);
      const current = groups[groups.length - 1];
      if (current?.label === label) current.items.push(item);
      else groups.push({ label, items: [item] });
    }
    return groups;
  }, [visibleItems]);

  const onMarkAll = async () => {
    await markNotificationsRead();
    setItems((prev) => prev.map(n => ({ ...n, Read: true })));
    setCount(0);
  };

  const markOne = async (n: Notification) => {
    if (n.Read) return;
    const r = await markNotificationsRead([n.Id]);
    setCount(r.unreadCount);
    setItems((prev) => prev.map(x => x.Id === n.Id ? { ...x, Read: true } : x));
  };

  const onClick = async (n: Notification) => {
    if (!n.Read) {
      try { await markOne(n); } catch { /* ignore */ }
    }
    setOpen(false);
    goTo(n);
  };

  return (
    <div className="relative" ref={popRef}>
      <Button
        variant="ghost"
        size="icon"
        onClick={onToggle}
        aria-label="Notifications"
        className="relative"
      >
        <Bell className="size-4" />
        {count > 0 && (
          <span
            className={cn(
              'absolute -top-1 -right-1 min-w-[16px] h-4 px-1 rounded-full',
              'bg-primary text-primary-foreground text-[10px] font-medium',
              'flex items-center justify-center',
            )}
          >
            {count > 99 ? '99+' : count}
          </span>
        )}
      </Button>

      {open && (
        <div className="absolute right-0 top-full mt-1 w-[28rem] max-w-[calc(100vw-1rem)] max-h-[70vh] bg-popover border rounded-md shadow-lg z-50 overflow-hidden flex flex-col">
          <div className="flex items-center justify-between gap-2 px-3 py-2 border-b">
            <div className="flex items-center gap-1">
              <button
                type="button"
                onClick={() => { setUnreadOnly(true); void refreshItems(true); }}
                className={cn(
                  'rounded px-2 py-0.5 text-xs',
                  unreadOnly ? 'bg-primary text-primary-foreground' : 'text-muted-foreground hover:bg-accent hover:text-foreground',
                )}
              >
                Unread
              </button>
              <button
                type="button"
                onClick={() => { setUnreadOnly(false); void refreshItems(false); }}
                className={cn(
                  'rounded px-2 py-0.5 text-xs',
                  !unreadOnly ? 'bg-primary text-primary-foreground' : 'text-muted-foreground hover:bg-accent hover:text-foreground',
                )}
              >
                All
              </button>
            </div>
            <div className="flex items-center gap-1">
              <button
                type="button"
                onClick={toggleDesktop}
                title={desktopOn ? 'Desktop alerts on' : 'Desktop alerts off'}
                aria-pressed={desktopOn}
                className={cn(
                  'rounded p-1',
                  desktopOn ? 'text-primary' : 'text-muted-foreground hover:text-foreground',
                )}
              >
                <Monitor className="size-3.5" />
              </button>
              <button
                type="button"
                onClick={toggleSound}
                title={soundOn ? 'Sound on' : 'Sound off'}
                aria-pressed={soundOn}
                className={cn(
                  'rounded p-1',
                  soundOn ? 'text-primary' : 'text-muted-foreground hover:text-foreground',
                )}
              >
                {soundOn ? <Volume2 className="size-3.5" /> : <VolumeX className="size-3.5" />}
              </button>
              {count > 0 && (
                <button
                  onClick={onMarkAll}
                  className="ml-1 text-xs text-muted-foreground hover:text-foreground"
                >Mark all read</button>
              )}
            </div>
          </div>
          <div className="flex gap-1 overflow-x-auto border-b px-2 py-1.5">
            <FilterButton active={kindFilter === 'all'} onClick={() => setKindFilter('all')}>All</FilterButton>
            {kindOptions.map(kind => (
              <FilterButton key={kind} active={kindFilter === kind} onClick={() => setKindFilter(kind)}>
                {KIND_LABEL[kind] ?? kind}
              </FilterButton>
            ))}
          </div>
          <div className="overflow-y-auto">
            {loading && items.length === 0 && (
              <div className="p-4 text-sm text-muted-foreground">Loading…</div>
            )}
            {visibleItems.length === 0 && !loading && (
              <div className="p-4 text-sm text-muted-foreground">All caught up.</div>
            )}
            {grouped.map(group => (
              <section key={group.label}>
                <div className="sticky top-0 z-10 border-b bg-popover px-3 py-1 text-[10px] font-medium uppercase text-muted-foreground">
                  {group.label}
                </div>
                {group.items.map(n => (
                  <button
                    key={n.Id}
                    onClick={() => onClick(n)}
                    className={cn(
                      'group flex w-full items-start gap-2 border-b px-3 py-2 text-left hover:bg-accent/50',
                      !n.Read && 'bg-accent/30',
                    )}
                  >
                    <span className={cn(
                      'mt-1.5 size-1.5 shrink-0 rounded-full',
                      n.Read ? 'bg-muted-foreground/30' : 'bg-primary',
                    )} />
                    <span className="min-w-0 flex-1">
                      <span className="block text-[11px] text-muted-foreground">
                        {KIND_LABEL[n.Kind] ?? n.Kind} · {compactTime(n.CreatedAt)}
                      </span>
                      <span className="block truncate text-sm">{n.Summary}</span>
                    </span>
                    {!n.Read && (
                      <span
                        role="button"
                        tabIndex={0}
                        onClick={(event) => {
                          event.stopPropagation();
                          void markOne(n);
                        }}
                        onKeyDown={(event) => {
                          if (event.key !== 'Enter' && event.key !== ' ') return;
                          event.preventDefault();
                          event.stopPropagation();
                          void markOne(n);
                        }}
                        className="mt-1 rounded p-1 text-muted-foreground opacity-0 hover:bg-accent hover:text-foreground group-hover:opacity-100 focus:opacity-100"
                        title="Mark read"
                      >
                        <Check className="size-3.5" />
                      </span>
                    )}
                  </button>
                ))}
              </section>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

function FilterButton({ active, onClick, children }: {
  active: boolean;
  onClick: () => void;
  children: ReactNode;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={cn(
        'shrink-0 rounded px-2 py-0.5 text-[11px]',
        active ? 'bg-muted text-foreground' : 'text-muted-foreground hover:bg-accent hover:text-foreground',
      )}
    >
      {children}
    </button>
  );
}

function notificationDayLabel(value: string): string {
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return 'Older';
  const today = new Date();
  const yesterday = new Date();
  yesterday.setDate(today.getDate() - 1);
  if (date.toDateString() === today.toDateString()) return 'Today';
  if (date.toDateString() === yesterday.toDateString()) return 'Yesterday';
  return 'Older';
}

function compactTime(value: string): string {
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '';
  return date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
}
